import {
  ChangeDetectionStrategy,
  Component,
  Input,
  ViewEncapsulation,
  EventEmitter,
  Output,
  HostListener,
  Renderer2,
} from '@angular/core';
import { ChevronRight, Plus } from './../icon/icon-map';
import { PhxNavCardSize } from './nav-card.types';

/**
 * Nav Card Component
 * 
 * Displays a navigation card with title, description, and optional features:
 * - Size variants: default, large
 * - Icon toggle: show/hide prefix icon
 * - Elevation: adds a shadow to the card and removes border
 * - Disabled state: disables interaction
 * 
 * @example
 * <phx-nav-card 
 *   phxTitle="Settings" 
 *   phxDescription="Configure your preferences"
 *   phxSize="large"
 *   [phxElevated]="true"
 *   [phxShowPrefixIcon]="true"
 *   (phxOnClick)="handleCardClick()">
 * </phx-nav-card>
 */

@Component({
  selector: 'phx-nav-card',
  styleUrls: ['./phx-nav-card.scss'],
  templateUrl: './phx-nav-card.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  inputs: [
  ],
  host: {
    class: 'phx-nav-card',
    '[class.phx-nav-card-elevated]': 'phxElevated',
    '[class.phx-nav-card-large]': 'phxSize === "large"',
    '[class.phx-nav-card-disabled]': 'phxDisabled',
    '[class.phx-has-prefix-icon]': 'phxShowPrefixIcon',
    '[class.phx-hide-description-on-mobile]': 'phxHideDescriptionOnMobile',
    '[class.phx-nav-card-full-width]': 'phxFullWidth',
    '[attr.tabindex]': '!phxDisabled ? 0 : -1',
    '[attr.aria-disabled]': 'phxDisabled',
    '(click)': 'phxDisabled ? null : phxOnClick.emit()',
    '(keyup.space)': 'phxDisabled ? null : phxOnClick.emit()',
    '(keyup.enter)': 'phxDisabled ? null : phxOnClick.emit()',
  },
})
export class PhxNavCardComponent {
  // Inputs
  /** The title text of the card */
  @Input() public phxTitle: string;

  /** Optional description text to display below the title */
  @Input() public phxDescription: string;

  /** Size variant of the card: 'default' or 'large' */
  @Input() public phxSize: PhxNavCardSize = 'default';

  /** Whether to add elevation (shadow) to the card */
  @Input() public phxElevated = false;

  /** Whether the card is in an inactive/disabled state */
  @Input() public phxDisabled = false;

  /** Whether to show prefix icon or not */
  @Input() public phxShowPrefixIcon = false;

  /** Which icon to show for the prefix */
  @Input() public phxPrefixIcon: string = Plus;

  /** Whether to hide the description for small screens */
  @Input() public phxHideDescriptionOnMobile = false;

  /** Whether the card takes the full width of its container (max 640px)*/
  @Input() public phxFullWidth: boolean = false;

  // Outputs
  /** Event emitted when the card is clicked or activated via keyboard */
  @Output() public phxOnClick: EventEmitter<void> = new EventEmitter<void>();

  public readonly ChevronRightIcon: string = ChevronRight;

  constructor(private renderer: Renderer2) { }

}
